import React, {FormEvent, ReactElement, useEffect, useState, MouseEvent, ReactNode} from 'react';

import {lighten} from 'polished';
import {useTranslation} from 'react-i18next';

import {ReactComponent as CrossIcon} from '../../../../../assets/svg/cross.svg';
import {ReactComponent as FilterIcon} from '../../../../../assets/svg/filter.svg';
import {I18N_NS} from '../../../../../core/constants/names';
import {colors} from '../../../../../theme';

import {InnerFilterValue} from '../../../../../types/grid2.0';
import {Button, ButtonVariant} from '../../../Button';
import {Icon} from '../../../Icon';
import {Popover} from '../../../Popover';

import {
    FilterIconContainer,
    FilterContent,
    ButtonsContainer,
    CloseButton,
    HeaderWrapper,
} from './styled';

interface WrapperProps {
    component: ReactElement;
    value: InnerFilterValue;
    onChange: (value: InnerFilterValue) => void;
    onReset: () => void;
}

const Wrapper = ({component, value, onChange, onReset}: WrapperProps) => {
    const {t} = useTranslation(I18N_NS);
    const [innerValue, setInnerValue] = useState<InnerFilterValue>(value);

    useEffect(() => {
        setInnerValue(value);
    }, [value]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onChange(innerValue);
    };

    const handleReset = (e: MouseEvent) => {
        e.preventDefault();
        onReset();
    };

    const handleChange = (data: any) => {
        // TODO input field has bad structure of onChange parameter
        if (data && data.target != null) {
            setInnerValue(data.target.value);
        } else {
            setInnerValue(data);
        }
    };

    // TODO fix uncontrolled -> controlled warning
    // TODO integrate validation
    return (
        <form onSubmit={handleSubmit}>
            {React.cloneElement(component, {
                ...component.props,
                value: innerValue,
                onChange: handleChange,
            })}

            <ButtonsContainer>
                <Button
                    variant={ButtonVariant.Secondary}
                    type="button"
                    onClick={handleReset}
                    size="sm"
                >
                    {t('components.table.Reset')}
                </Button>
                <Button variant={ButtonVariant.Normal} type="submit" size="sm">
                    {t('components.table.Submit')}
                </Button>
            </ButtonsContainer>
        </form>
    );
};

interface Props extends WrapperProps {
    header: ReactNode;
    parentRef?: ReactElement;
}

const headerOffset = {x: 20, y: 5};

export const Filtration = ({header, component, onChange, onReset, value, parentRef}: Props) => {
    const [opened, setOpened] = useState(false);

    const handleChange = (value: InnerFilterValue) => {
        onChange(value);
        setOpened(false);
    };

    const handleReset = () => {
        onReset();
        setOpened(false);
    };

    return (
        <>
            <FilterIconContainer
                active={opened}
                fillColor={
                    opened
                        ? colors.white
                        : value == null
                        ? lighten(0.1, colors.textSecondary)
                        : colors.textPrimary
                }
                onClick={() => setOpened(!opened)}
                source={FilterIcon}
                width={20}
                height={20}
                block={false}
            />

            <Popover
                isOpen={opened}
                onClose={() => setOpened(false)}
                // @ts-ignore
                pivotElement={parentRef}
                offset={headerOffset}
            >
                <FilterContent>
                    <CloseButton
                        icon={
                            <Icon source={CrossIcon} fillColor="#C4C4C4" width={12} height={12} />
                        }
                        onClick={() => setOpened(false)}
                    />
                    <HeaderWrapper>{header}</HeaderWrapper>
                    <Wrapper
                        component={component}
                        value={value}
                        onChange={handleChange}
                        onReset={handleReset}
                    />
                </FilterContent>
            </Popover>
        </>
    );
};
